import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import MainLayout from "../components/layout/MainLayout";
import EventList from "../features/events/EventList";
import EventForm from "../features/events/EventForm";
import EventDetailModal from "../components/common/EventDetailModal";
import { useNotification } from "../context/NotificationContext";
import eventService from "../services/eventService";

const Events = () => {
  const { t } = useTranslation(["events", "common"]);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [statisticsData, setStatisticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { showSuccess, showError } = useNotification();

  const handleCreate = () => {
    setEditingEvent(null);
    setShowForm(true);
  };

  const handleEdit = async (event) => {
    try {
      setLoading(true);
      console.log("Loading event for edit:", event.id);
      const response = await eventService.getEvent(event.id);
      console.log("Event edit response:", response);

      const eventData = response.data || response;
      console.log("Setting editing event:", eventData);
      setEditingEvent(eventData);
      setShowForm(true);
    } catch (error) {
      console.error("Error loading event details:", error);
      showError(t("events:messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (event) => {
    try {
      setLoading(true);
      const response = await eventService.getEvent(event.id);

      // Handle the response properly based on your backend structure
      const eventData = response.data || response;
      setViewingEvent(eventData);
    } catch (error) {
      console.error("Error loading event details:", error);
      showError(t("events:messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewStatistics = (event) => {
    if (viewingEvent?.statistics) {
      setStatisticsData(viewingEvent.statistics);
      setShowStatistics(true);
    }
  };

  const handleCloseView = () => {
    setViewingEvent(null);
  };

  const handleCloseStatistics = () => {
    setShowStatistics(false);
    setStatisticsData(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      let response;

      if (editingEvent) {
        response = await eventService.updateEvent(editingEvent.id, formData);
        if (response) {
          showSuccess(t("events:messages.updateSuccess"));
        }
      } else {
        response = await eventService.createEvent(formData);
        if (response) {
          showSuccess(t("events:messages.createSuccess"));
        }
      }

      setShowForm(false);
      setEditingEvent(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingEvent(null);
  };

  return (
    <MainLayout>
      <div className="events-page">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">{t("events:management")}</h1>
            <p className="text-muted mb-0">
              {t("events:managementSubtitle")}
            </p>
          </div>
          <button className="btn btn-primary" onClick={handleCreate}>
            <i className="fas fa-plus me-2"></i>
            {t("events:addEvent")}
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i
                      className={`fas ${
                        editingEvent ? "fa-edit" : "fa-plus"
                      } me-2`}
                    ></i>
                    {editingEvent ? t("events:editEvent") : t("events:addNewEvent")}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleFormCancel}
                    disabled={loading}
                  ></button>
                </div>
                <div className="modal-body">
                  <EventForm
                    event={editingEvent}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormCancel}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Event Detail Modal */}
        {viewingEvent && (
          <EventDetailModal
            event={viewingEvent}
            onClose={handleCloseView}
            onEdit={(ev) => { handleCloseView(); handleEdit(ev); }}
            onViewStatistics={handleViewStatistics}
            statisticsData={showStatistics ? statisticsData : null}
            onCloseStatistics={handleCloseStatistics}
          />
        )}

        {/* Events List */}
        <EventList
          onEdit={handleEdit}
          onView={handleView}
          refreshTrigger={refreshTrigger}
        />
      </div>
    </MainLayout>
  );
};

export default Events;
